import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@redux";
import {
    clearSignupPendingUpgrade,
    ensurePointsAgentUpgrade,
    readSignupPendingUpgrade,
} from "../pointsAgentUpgrade";

/**
 * Completes a sign-up's points-agent upgrade once the account is verified.
 *
 * The `/signup` page (reached from the burger menu) cannot upgrade where it
 * creates the account: the email is unverified at that point and the visitor is
 * immediately routed away to the verification screen, so the component that knew
 * about the sign-up is gone by the time verification succeeds. This watches the
 * account instead, and fires when the flag left behind by `markSignupPendingUpgrade`
 * meets an account that is verified and not yet an agent.
 *
 * Mounted once in App.tsx, so it covers whichever screen the visitor lands on.
 */
export const usePointsAgentUpgrade = () => {
    const dispatch = useAppDispatch();
    const account = useAppSelector((state) => state.account.account);
    // Guards against a second run while the first is still in flight: the upgrade
    // dispatches account refreshes, which re-enter this effect before the flag clears.
    const isUpgrading = useRef(false);

    useEffect(() => {
        const pendingEmail = readSignupPendingUpgrade();
        if (!pendingEmail || isUpgrading.current || !account) return;

        if (account.is_agent_integrated) {
            clearSignupPendingUpgrade();
            return;
        }
        if (!account.is_email_verified) return;

        isUpgrading.current = true;
        ensurePointsAgentUpgrade({ email: pendingEmail, account, dispatch }).finally(() => {
            // Cleared either way. A failed upgrade should not be retried on every
            // account refresh for the rest of the session; the traveller form's
            // "Save and Earn Points" remains the fallback route.
            clearSignupPendingUpgrade();
            isUpgrading.current = false;
        });
    }, [account, dispatch]);
};
